/**
 * Complete ERPNext operator mapping
 * Based on frappe/public/js/frappe/ui/filters/filter.js
 */
export type FilterOperator =
  | "equals" // =
  | "notEquals" // !=
  | "like" // like
  | "notLike" // not like
  | "in" // in
  | "notIn" // not in
  | "greaterThan" // >
  | "lessThan" // <
  | "greaterOrEqual" // >=
  | "lessOrEqual" // <=
  | "between" // between
  | "isSet" // != "" (is not empty)
  | "isNotSet" // = "" (is empty)
  | "is" // is (for None/not None)
  | "timespan"; // timespan (for date ranges)

export type FilterValue =
  | string
  | number
  | boolean
  | string[]
  | number[]
  | [number, number] // for between numbers
  | [string, string] // for between dates
  | null;

export type FilterDescriptor = {
  field: string;
  operator: FilterOperator;
  value: FilterValue;
  /**
   * Optional child doctype for 4-tuple filters
   * When present, indicates this filter targets a child table field
   * Format: [child_doctype, field, operator, value]
   */
  childDoctype?: string;
  _metadata?: {
    section?: "assigned_to" | "created_by" | "tags" | "general";
    logicMode?: "any" | "all";
  };
};

/**
 * ERPNext operator labels (exact match)
 */
export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  equals: "Equals",
  notEquals: "Not Equals",
  like: "Like",
  notLike: "Not Like",
  in: "In",
  notIn: "Not In",
  greaterThan: "Greater Than",
  lessThan: "Less Than",
  greaterOrEqual: "Greater Than Or Equal To",
  lessOrEqual: "Less Than Or Equal To",
  between: "Between",
  isSet: "Is Set",
  isNotSet: "Is Not Set",
  is: "Is",
  timespan: "Timespan",
};

/**
 * Special date/datetime operator labels
 */
export const DATE_OPERATOR_LABELS: Record<string, string> = {
  "<": "Before",
  ">": "After",
  "<=": "On or Before",
  ">=": "On or After",
};

/**
 * Operators by field type (from ERPNext filter.js)
 * Invalid operators are excluded per field type
 */
export const OPERATORS_BY_FIELDTYPE: Record<string, FilterOperator[]> = {
  // Text fields
  Data: ["equals", "notEquals", "like", "notLike", "in", "notIn", "isSet", "isNotSet"],
  "Small Text": ["equals", "notEquals", "like", "notLike", "in", "notIn", "isSet", "isNotSet"],
  Text: ["equals", "notEquals", "like", "notLike", "in", "notIn", "isSet", "isNotSet"],
  "Long Text": ["equals", "notEquals", "like", "notLike", "in", "notIn", "isSet", "isNotSet"],

  // Numeric fields
  Int: ["equals", "notEquals", "greaterThan", "lessThan", "greaterOrEqual", "lessOrEqual", "between", "in", "notIn"],
  Float: ["equals", "notEquals", "greaterThan", "lessThan", "greaterOrEqual", "lessOrEqual", "between", "in", "notIn"],
  Currency: ["equals", "notEquals", "greaterThan", "lessThan", "greaterOrEqual", "lessOrEqual", "between", "in", "notIn"],

  // Boolean
  Check: ["equals", "notEquals"],

  // Select/Link
  Select: ["equals", "notEquals", "in", "notIn", "like"],
  Link: ["equals", "notEquals", "in", "notIn", "like"],

  // Date fields
  Date: ["equals", "notEquals", "greaterThan", "lessThan", "greaterOrEqual", "lessOrEqual", "between", "isSet", "isNotSet", "timespan"],
  Datetime: ["equals", "notEquals", "greaterThan", "lessThan", "greaterOrEqual", "lessOrEqual", "between", "isSet", "isNotSet", "timespan"],
  Time: ["equals", "notEquals", "greaterThan", "lessThan", "greaterOrEqual", "lessOrEqual", "between"],

  // Other
  Code: ["equals", "notEquals", "like", "notLike", "in", "notIn", "isSet", "isNotSet"],
  Color: ["equals", "notEquals", "in", "notIn"],
  Rating: ["equals", "notEquals", "like", "notLike", "between", "in", "notIn", "timespan"],
};

/**
 * Get valid operators for a field type
 */
export function getOperatorsForFieldtype(fieldtype: string): FilterOperator[] {
  return OPERATORS_BY_FIELDTYPE[fieldtype] || ["equals", "notEquals"];
}

